import { getCenteredScrollPosition } from '@common/services';
import type { ColorsType } from '@common/tokens';
import type {
  ScheduleDemandType,
  ScheduleRoleType,
  ScheduleShiftTypeSectionKeyType,
  ScheduleShiftType,
} from '@load-balancing/types';

/** Color-token accent per shift-type section — drives the left stripe and section icon. */
export const SHIFT_TYPE_SECTION_ACCENT: Record<
  ScheduleShiftTypeSectionKeyType,
  ColorsType
> = {
  day: 'sunDarker',
  evening: 'harvestGold',
  night: 'skyDarker',
};

// Within a day cell, shifts sort by: (1) PTO always first, (2) startHour ascending,
// (3) SOURCE_PRIORITY as a tiebreaker. PTO is pinned to the top regardless of its
// actual start_time because vendor PTO records often have a different start_time than
// the section template, and the displayed time on every card is the section label anyway.
const SOURCE_PRIORITY: Record<ScheduleShiftType['source'], number> = {
  pto: 0,
  pattern: 1,
  self: 5,
  manual: 6,
  balancer: 7,
  float: 8,
  open: 9,
};

export function sortShifts(shifts: ScheduleShiftType[]): ScheduleShiftType[] {
  return [...shifts].sort(
    (a, b) =>
      Number(a.source !== 'pto') - Number(b.source !== 'pto') ||
      a.startHour - b.startHour ||
      SOURCE_PRIORITY[a.source] - SOURCE_PRIORITY[b.source]
  );
}

/** Shifts for a single section × role × day, sorted for display. */
export function cellShifts(
  shifts: ScheduleShiftType[],
  section: ScheduleShiftTypeSectionKeyType,
  role: ScheduleRoleType,
  dayKey: string
): ScheduleShiftType[] {
  return sortShifts(
    shifts.filter(
      (shift) =>
        shift.shiftTypeSection === section &&
        shift.role === role &&
        shift.dayKey === dayKey
    )
  );
}

/**
 * Whether a shift counts toward the cell's `filled / planned` numerator —
 * i.e. a worker physically staffing the slot. Excludes open slots (no worker)
 * and non-working blocks: PTO, leave, absence, standby, on-call (all carry
 * `fixedCommitment`, set server-side from the worked-coverage lens — not FTE
 * contribution, so FTE-accruing leave like military leave is still excluded).
 * Mirrors the reassign-eligibility check in `worker-availability.ts`.
 */
export function isCoverageShift(shift: ScheduleShiftType): boolean {
  return (
    Boolean(shift.workerName) &&
    shift.source !== 'pto' &&
    !shift.fixedCommitment
  );
}

/**
 * Planned-demand headcount for a single section × role × day — the cell's count
 * denominator. Sibling of {@link cellShifts}; 0 when the cell has no demand row.
 */
export function cellDemand(
  demands: ScheduleDemandType[],
  section: ScheduleShiftTypeSectionKeyType,
  role: ScheduleRoleType,
  dayKey: string
): number {
  return (
    demands.find(
      (demand) =>
        demand.shiftTypeSection === section &&
        demand.role === role &&
        demand.dayKey === dayKey
    )?.plannedCount ?? 0
  );
}

/**
 * Smooth-scrolls the grid so the card for `shiftId` is centered in the scroll
 * container. No-op when the container or card isn't found (e.g. the shift is
 * filtered out). Explicit container scroll math — `scrollIntoView` is unreliable
 * inside the `<table>` layout, where scroll-ancestor detection can misfire.
 */
export function scrollShiftCardIntoView(
  root: HTMLElement | null | undefined,
  shiftId: string
): void {
  /** Scroll container the grid renders into (PlannerCalendar's scroll region). */
  const SCROLL_CONTAINER_SELECTOR = '.wk-planner-calendar__scroll';
  const shiftIdSelector = `[data-shift-id="${shiftId}"]`;

  const container = root?.querySelector<HTMLElement>(SCROLL_CONTAINER_SELECTOR);
  const card = container?.querySelector<HTMLElement>(shiftIdSelector);
  if (!container || !card) return;

  container.scrollTo({
    ...getCenteredScrollPosition(container, card),
    behavior: 'smooth',
  });
}
